import { ApiChallenge } from "griptonite-api-client";
import groupBy from "lodash/groupBy";
import { useEffect } from "react";

import useLocalStorage from "@/hooks/useLocalStorage";

type Warning = {
  message: string;
  field?: string;
  tab: string;
};

export default function getPublishWarnings(challenge?: ApiChallenge) {
  const warnings: Warning[] = [];

  if (!challenge || !challenge.isHidden) {
    return {
      allWarnings: warnings,
      byTab: {},
    };
  }

  if (!challenge.description) {
    warnings.push({
      message: "Missing description",
      tab: "details",
    });
  }

  if (!challenge.coverImage) {
    warnings.push({
      message: "No cover image has been uploaded. A cover image is required",
      tab: "details",
    });
  }

  if (challenge.tiers.length === 0) {
    warnings.push({
      message: "No reward tiers have been configured",
      tab: "rules",
    });
  }

  return {
    allWarnings: warnings,
    byTab: groupBy(warnings, "tab"),
  };
}

export const useHideWarningsMap = (
  challengeUuid?: string,
): Record<string, boolean | null | undefined> => {
  const localStorageValues = useLocalStorage({
    keys: [
      `challenges:${challengeUuid}:hideWarnings:details`,
      `challenges:${challengeUuid}:hideWarnings:rules`,
      `challenges:${challengeUuid}:hideWarnings:promotion`,
    ],
  });
  return {
    details: localStorageValues[`challenges:${challengeUuid}:hideWarnings:details`],
    rules: localStorageValues[`challenges:${challengeUuid}:hideWarnings:rules`],
    promotion: localStorageValues[`challenges:${challengeUuid}:hideWarnings:promotion`],
  } as Record<string, boolean | null | undefined>;
};

/**
 * Unhide warnings from the previous tab when the component mounts.
 */
export function useUnhidePreviousTabWarningsOnMount(challengeUuid: string, tab: string) {
  const warningKey = `challenges:${challengeUuid}:hideWarnings:${tab}`;

  const { setItem: localStorageSetItem } = useLocalStorage({
    keys: [warningKey],
  });

  useEffect(() => {
    localStorageSetItem(warningKey, false);
  }, [challengeUuid, localStorageSetItem, warningKey]);

  return localStorageSetItem;
}
